import './LandingMainSection.scss'
import brandLogo from '../../../../assets/images/branding/gg_landing_brand.png'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useState, useEffect } from 'react';
import GenieAIChatWindow from './GenieAIChatWindow';
import { Typography } from '@mui/material';

// Button texts that will cycle through as placeholder - moved outside component
const buttonTexts = [
  'I need a website for my restaurant',
  'Build an App',
  'Design UI/UX',
  'Instagram reel for promotion',
  'Design menu for Cloud Kitchen'
];

const LandingMainSection = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isTransforming, setIsTransforming] = useState(false);
  const [placeholderText, setPlaceholderText] = useState('');
  const [showCursor, setShowCursor] = useState(true);

  const scrollToNextSection = () => {
    const nextSection = document.querySelector('.reimagineSection');
    if (nextSection) {
      const elementPosition = nextSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 60; // 80px offset from top

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleRubTheLamp = () => {
    setIsTransforming(true);
    
    // Add magical transformation effect with swoosh
    setTimeout(() => {
      setIsChatOpen(true);
      // Keep transforming state for a bit longer to show the morph effect
      setTimeout(() => {
        setIsTransforming(false);
      }, 100);
    }, 600);
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleButtonClick = (text: string) => {
    setSearchValue(text);
  };

  const handleSearchFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Select all text when clicking on search bar
    e.target.select();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isTransforming) {
      handleRubTheLamp();
    }
  };

  // Animated placeholder effect with typing and backspace
  useEffect(() => {
    let currentIndex = 0;
    let currentText = '';
    let isDeleting = false;
    let timeoutId: NodeJS.Timeout;
    let isRunning = true;

    const typeText = () => {
      if (!isRunning) return;
      
      const fullText = buttonTexts[currentIndex];
      
      if (isDeleting) {
        // Deleting text character by character
        currentText = fullText.substring(0, currentText.length - 1);
        setPlaceholderText(currentText);
        
        if (currentText === '') {
          isDeleting = false;
          currentIndex = (currentIndex + 1) % buttonTexts.length;
          timeoutId = setTimeout(typeText, 1000); // Wait 1 second before typing next
        } else {
          timeoutId = setTimeout(typeText, 30); // Faster deletion speed
        }
      } else {
        // Typing text character by character
        currentText = fullText.substring(0, currentText.length + 1);
        setPlaceholderText(currentText);
        
        if (currentText === fullText) {
          isDeleting = true;
          timeoutId = setTimeout(typeText, 2000); // Wait 2 seconds before deleting
        } else {
          timeoutId = setTimeout(typeText, 30); // Faster typing speed
        }
      }
    };

    // Start the animation immediately - no delay
    typeText();

    return () => {
      isRunning = false;
      clearTimeout(timeoutId);
    };
  }, []); // Empty dependency array - only run once on mount

  // Stop animation when user types
  useEffect(() => {
    if (searchValue.trim() !== '') {
      setPlaceholderText('');
    }
  }, [searchValue]);

  // Blinking cursor effect
  useEffect(() => {
    if (searchValue.trim() !== '') {
      return;
    }

    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);

    return () => clearInterval(cursorInterval);
  }, [searchValue]);

  return (
    <section className="landingMainSection">
      {/* Main centered content */}
      <div className='spacer'></div>
      <div className="landingMainSection__mainContent">
        <div className="landingMainSection__logoRow">
          <img
            className="landingMainSection__logo"
            src={brandLogo}
            alt="GenieGig logo"
          />
        </div>

        <p className="landingMainSection__tagline">
          <span className="sparkle"><AutoAwesomeIcon/></span> AI Powered <strong>Freelance Revolution</strong> is here
        </p>

        <div className={`landingMainSection__search ${isTransforming ? 'transforming' : ''}`}>
          <span className="landingMainSection__aiLabel">
          <AutoAwesomeIcon/> <Typography fontFamily={'Goodfish'} fontWeight={900}>GenieAI</Typography> 
            <span className="beta-tag">BETA</span>
          </span>
          <input
            className="landingMainSection__input"
            placeholder={searchValue.trim() === '' ? `${placeholderText}${showCursor ? '|' : ''}` : ''}
            value={searchValue}
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
            onKeyPress={handleKeyPress}
            readOnly={isTransforming}
          />
          <button 
            className={`landingMainSection__cta ${isTransforming ? 'transforming' : ''}`}
            onClick={handleRubTheLamp}
            disabled={isTransforming}
            style={{justifyContent:'center'}}
          >
            {isTransforming ? (
              <>
                <div className="magic-sparkles">
                  <span>✨</span>
                </div>
                <span>Summoning GenieAI...</span>
              </>
            ) : (
              <span className="Rub-the-lamp-btn" style={{ fontWeight:'bolder', display: 'inline-flex', alignItems: 'center'}}>
                Rub the lamp
              </span>
            )}
          </button>
        </div>

        <div className="landingMainSection__quickChips">
          {buttonTexts.map((text, index) => (
            <button 
              key={index}
              className="chip" 
              onClick={() => handleButtonClick(text)}
            >
              {text}
            </button>
          ))}
        </div>

        <div className="landingMainSection__explore">
          <button onClick={scrollToNextSection} className="exploreBtn">Explore Now</button>
          <div onClick={scrollToNextSection} className="caret">▾</div>
        </div>
      </div>

      {/* Bottom text positioned at bottom */}
      <div className='landingMainSection__bottomText'>
        <p className="landingMainSection__subtitle">
          Teleport from <strong style={{color:"#EF4D00"}}>Startup</strong> to <strong style={{color:"#EF4D00"}}>Enterprise</strong> in no time!
        </p>
        <p className="landingMainSection__subcopy">
          Connect with top freelancers instantly. <strong>GenieAI</strong> understands your project and
          matches you with the perfect pool of talent in seconds.
        </p>
      </div>

      {/* GenieAI Chat Window */}
      <GenieAIChatWindow
        isOpen={isChatOpen}
        onClose={handleCloseChat}
        initialMessage={searchValue.trim() !== '' ? searchValue : 'Hi GenieAI'}
      />
    </section>
  )
}

export default LandingMainSection


